import { notification } from 'antd';

type NotificationType = 'success' | 'info' | 'warning' | 'error';

interface ToasterProps {
  type: NotificationType;
  message: string;
  description?: string;
}

// Ensure there is a live region in the document for screen readers (NVDA, JAWS, VoiceOver)
const ensureLiveRegion = () => {
  const id = 'a11y-notification-live-region';
  let region = document.getElementById(id);
  if (!region) {
    region = document.createElement('div');
    region.id = id;
    region.setAttribute('aria-atomic', 'true');
    // Visually hide but keep accessible
    region.style.position = 'absolute';
    region.style.width = '1px';
    region.style.height = '1px';
    region.style.margin = '-1px';
    region.style.border = '0';
    region.style.padding = '0';
    region.style.overflow = 'hidden';
    region.style.clip = 'rect(0 0 0 0)';
    region.style.whiteSpace = 'nowrap';
    document.body.appendChild(region);
  }
  return region;
};

const setRegionPoliteness = (region: Element, politeness: 'assertive' | 'polite') => {
  try {
    if (politeness === 'assertive') {
      region.setAttribute('aria-live', 'assertive');
      region.setAttribute('role', 'alert');
    } else {
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('role', 'status');
    }
  } catch (e) {
    // ignore
  }
};

const useToasterMessage = () => {
  const UseToaster = ({ type, message, description }: ToasterProps) => {
    // Only update an ARIA live region so screen readers announce the message reliably
    try {
      const region = ensureLiveRegion();
      // error -> assertive, success/info -> polite
      const politeness = type === 'error' ? 'assertive' : 'polite';
      setRegionPoliteness(region, politeness);
      const text = `${message}${description ? ' - ' + description : ''}`;
      if (process.env.NODE_ENV === 'development') {
        // small debug to verify region updates during local testing
        // eslint-disable-next-line no-console
        console.log('[a11y] updating live region:', politeness, text);
      }
      // Setting textContent triggers screen readers to announce changes
      region.textContent = text;
      // For assertive messages, briefly focus the live region then restore previous focus.
      // This improves announcement reliability with NVDA in some browsers.
      try {
        if (politeness === 'assertive') {
          const prevActive = document.activeElement as HTMLElement | null;
          (region as HTMLElement).setAttribute('tabindex', '-1');
          (region as HTMLElement).focus();
          window.setTimeout(() => {
            try {
              if (prevActive && typeof (prevActive as any).focus === 'function') {
                (prevActive as HTMLElement).focus();
              } else {
                // remove tabindex if nothing to restore
                (region as HTMLElement).removeAttribute('tabindex');
              }
            } catch (e) {
              // ignore
            }
          }, 400);
        }
      } catch (e) {
        // ignore focus errors
      }
     
      window.setTimeout(() => {
        region.textContent = '';
      }, 5000);
    } catch (e) {
      // ignore DOM errors in non-browser environments
    }
  };

  return { UseToaster };
};

export default useToasterMessage;
